import { devicesApi } from 'app/services/api';
import _ from 'lodash';

import { logger } from 'utils/logger';
import { getIsNativePlatform } from 'utils/platform';

import {
  DEVICE_PUSH_TAG,
  HEARTBEAT_INTERVAL_MS,
  VOIP_PUSH_PLATFORM,
} from './deviceTelemetry.constants';
import { getTelemetryContext } from './deviceTelemetry.utils';

class DeviceTelemetry {
  #heartbeatIntervalId = null;

  pingOta = async () => {
    if (!getIsNativePlatform()) {
      return;
    }

    try {
      const context = await getTelemetryContext();

      await devicesApi.otaPing({
        deviceUuid: context.deviceUuid,
        appId: context.appId,
        channel: context.channel,
        platform: context.platform,
        bundleVersion: context.bundleVersion,
        appNativeVersion: context.appNativeVersion,
        versionBuild: context.versionBuild,
        isEmulator: context.isEmulator,
      });
    } catch (error) {
      logger.error('[DeviceTelemetry] ota-ping failed', error);
    }
  };

  register = async ({
    pushToken = null,
    pushPlatform = null,
    pushTag = null,
    biometricEnrolled = false,
  } = {}) => {
    if (!getIsNativePlatform()) {
      return;
    }

    try {
      const context = await getTelemetryContext();

      await devicesApi.register({
        deviceUuid: context.deviceUuid,
        platform: context.platform,
        osVersion: context.osVersion,
        model: context.model,
        manufacturer: context.manufacturer,
        isEmulator: context.isEmulator,
        appNativeVersion: context.appNativeVersion,
        appNativeBuild: context.versionBuild,
        bundleVersion: context.bundleVersion,
        bundleId: context.appId,
        locale: context.locale,
        pushToken,
        pushPlatform,
        pushTag,
        biometricEnrolled,
      });
    } catch (error) {
      logger.error('[DeviceTelemetry] register failed', error);
    }
  };

  // Registers the iOS VoIP (PushKit) token with the device registry under the `voip` tag — mirrors
  // register() but routes to a separate push binding (platform 'ios-voip') so it never clobbers the
  // standard alert push token. The token is the device the backend targets for inbound ring-both.
  registerVoipToken = ({ voipToken = null, platform = VOIP_PUSH_PLATFORM } = {}) => {
    if (_.isNil(voipToken)) {
      return Promise.resolve();
    }

    return this.register({
      pushToken: voipToken,
      pushPlatform: platform,
      pushTag: DEVICE_PUSH_TAG.VOIP,
    });
  };

  sendHeartbeat = async () => {
    if (!getIsNativePlatform()) {
      return;
    }

    try {
      const context = await getTelemetryContext();

      await devicesApi.heartbeat({
        deviceUuid: context.deviceUuid,
        appNativeVersion: context.appNativeVersion,
        osVersion: context.osVersion,
        bundleVersion: context.bundleVersion,
      });
    } catch (error) {
      logger.error('[DeviceTelemetry] heartbeat failed', error);
    }
  };

  startHeartbeat = () => {
    if (!getIsNativePlatform() || !_.isNull(this.#heartbeatIntervalId)) {
      return;
    }

    this.sendHeartbeat();

    this.#heartbeatIntervalId = globalThis.setInterval(this.sendHeartbeat, HEARTBEAT_INTERVAL_MS);
  };

  stopHeartbeat = () => {
    if (_.isNull(this.#heartbeatIntervalId)) {
      return;
    }

    globalThis.clearInterval(this.#heartbeatIntervalId);
    this.#heartbeatIntervalId = null;
  };
}

export default DeviceTelemetry;
